import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useOriginalSubmissionFileDownload } from "#src/components/hooks/adapters/useTransactions";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";
import { useListWorkerRunReports } from "#src/components/hooks/adapters/useWorkers";
import {
  useClientSideSortingAndPagination,
  useTableSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import { DataSetSubmissionDetailRoutePath } from "#src/routes/data-tools/submissions/datasets/[transactionId]/detail";
import {
  DataTable,
  DataTablePanel,
  type HeaderType,
  type ItemAction,
} from "@validereinc/common-components";
import {
  SortDirection,
  UserTypeEnum,
  type WorkerRunReportType,
} from "@validereinc/domain";
import React, { useMemo } from "react";

type WorkerRunReportsTablePanelProps = {
  runId?: string;
  tableConfigStorageKey: string;
  tableStateStorageKey: string;
};

export const WorkerRunReportsTablePanel = ({
  runId,
  tableConfigStorageKey,
  tableStateStorageKey,
}: WorkerRunReportsTablePanelProps) => {
  const hasRunId = Boolean(runId);

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    { runId },
    tableStateStorageKey
  );

  const {
    data: allReportRows,
    isLoading: isLoadingReports,
    isFetching: isFetchingReports,
  } = useListWorkerRunReports({ id: runId ?? "" }, { enabled: hasRunId });

  const { items, pagination, sorting } = useClientSideSortingAndPagination(
    allReportRows,
    tableState
  );

  const uniqueCreatedByIds = useMemo(() => {
    const ids = new Set<string>();
    (allReportRows ?? []).forEach((row) => {
      ids.add(row.created_by);
    });
    return Array.from(ids);
  }, [allReportRows]);

  const { data: usersData, isLoading: isLoadingUsers } = useListUsers(
    {
      filters: {
        id: uniqueCreatedByIds,
        type: [UserTypeEnum.human, UserTypeEnum.machine],
      },
    },
    {
      enabled: hasRunId && uniqueCreatedByIds.length > 0,
    }
  );

  const usersById = useMemo(
    () =>
      Object.fromEntries(
        (usersData?.data ?? []).map((user) => [user.id, user])
      ),
    [usersData]
  );

  const isLoading =
    isLoadingReports || (uniqueCreatedByIds.length > 0 && isLoadingUsers);

  const downloadOriginalFileMutation = useOriginalSubmissionFileDownload();

  const headers: Array<HeaderType<WorkerRunReportType>> = [
    {
      key: "name",
      label: "Name",
      isSortable: false,
      renderComponent: ({ item }) => (
        <RoutingLink
          to={DataSetSubmissionDetailRoutePath.toLinkParts({
            pathParams: { transactionId: item.transaction_id },
          })}
        >
          {item.name}
        </RoutingLink>
      ),
    },
    {
      key: "created_at",
      label: "Created At",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          withTime
        />
      ),
    },
    {
      key: "created_by",
      label: "Created By",
      isSortable: false,
      renderComponent: ({ item }) => {
        const user = usersById[item.created_by];
        return (
          <UserDataDisplay
            id={item.created_by}
            name={user?.name ?? "—"}
            isLoading={isLoading}
          />
        );
      },
    },
  ];

  const getItemActions = ({
    item,
  }: {
    item: WorkerRunReportType;
  }): ItemAction[] => [
    {
      label: "Download Original Submission",
      buttonProps: {
        onClick: () =>
          downloadOriginalFileMutation.mutate({
            transactionId: item.transaction_id,
            fileName: `${item.name}-${item.transaction_id}.json`,
          }),
        icon: "download",
      },
    },
  ];

  const emptyStateProps = hasRunId
    ? {
        title: "No reports available.",
        suggestion: "This worker run has not produced any reports yet.",
      }
    : {
        title: "Run not found.",
        suggestion:
          "This worker run does not exist or you may not have access to it.",
      };

  return (
    <DataTablePanel
      key={runId ?? "run-reports"}
      storageKey={tableConfigStorageKey}
      panelProps={{ title: "Reports" }}
      dataTableProps={{
        intent: "simplicity-first",
        headers,
        items: hasRunId ? items : [],
        getItemId: (item) => `${item.worker_run_id}-${item.transaction_id}`,
        getItemActions,
        sorting,
        pagination,
        isLoading: hasRunId && isLoading,
        isBusy: isFetchingReports,
        emptyStateProps,
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
      }}
    />
  );
};
